<template>
  <span />
</template>
<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('PositionTracker.vue');

import TextUtils from '@shared/publication/dom-utils/text-utils.mjs';
import PositionTrackerEvents from '@/enums/PositionTrackerEvents';
import ProgressTrackingTask from '@/enums/ProgressTrackingTask';
import TrackerStateEnum from '@/enums/TrackerStateEnum';
import BookScrollEnum from '@/enums/BookScrollEnum';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';

import Locator from '@shared/publication/locator.mjs';
import UserProgressFactory from '@/classes/factories/UserProgressFactory';
class PositionTracker {
  constructor(readingSpeed, bookId, publicationType, type, options) {
    this.position = null;
    this.startPosition = null;
    this.readingSpeed = readingSpeed;
    this.type = type;
    this.publicationType = publicationType;
    this.scheduled = null;
    this.wordElementsOffsets = [];
    this.currentWordIndex = 0;
    this.contentStableLength = undefined;
    this.wordsCountInPara = 0;
    this.store = options.store;
    this.bookId = bookId;
  }

  getNeedHighlight() {
    return this.store.getters[
      'ReadingSettingsStore/getReadingPositionTracking'
    ];
  }

  getHighlightClass() {
    return this.store.getters['ProgressStore/getDefaultHighLightClass'];
  }

  getCurrentWordElementOffset() {
    return this.wordElementsOffsets[this.currentWordIndex] || [0, 0];
  }

  getCurrentWordOffset() {
    const currentWordElementOffset = this.getCurrentWordElementOffset();
    return currentWordElementOffset[1] - currentWordElementOffset[0];
  }

  moveNextIndex() {
    this.currentWordIndex += 1;
  }

  isLast() {
    return Boolean(!this.wordElementsOffsets[this.currentWordIndex + 1]);
  }

  setNextWord() {
    const currentWordElementOffset = this.getCurrentWordElementOffset();
    this.position.logicalCharOffset = currentWordElementOffset[1];
    this.moveNextIndex();
  }

  setNextPara(readParagraphRanges) {
    const nextParaId = this._getNextParaId(this.position.prefixedParagraphId);
    if (!nextParaId) {
      return false;
    }
    const logicalCharOffset = 0;
    const isSetNextPara = this.fillParagraphData(
      nextParaId,
      logicalCharOffset,
      readParagraphRanges[nextParaId]
    );
    return isSetNextPara;
  }

  getReadWordTime() {
    const currentWordOffset = this.getCurrentWordOffset();
    return currentWordOffset * this.readingSpeed;
  }

  isInsideReadingArea() {
    return this.store.getters['ProgressStore/isInsideReadingArea'](
      this.position
    );
  }

  fillParagraphData(startParaId, startLogicalCharOffset, readRanges) {
    let isSetNextPara = true;
    try {
      const para = document.getElementById(startParaId);
      const wordElementsOffsets = this._getAllWordsFromPara(para, readRanges);

      const wordElementsOffsetsIndex = wordElementsOffsets.findIndex(offset => {
        return offset[0] >= startLogicalCharOffset;
      });
      const logicalCharOffset =
        wordElementsOffsets[wordElementsOffsetsIndex][0];

      this.wordElementsOffsets = wordElementsOffsets;
      this.position = new Locator.InTextLocator(startParaId, logicalCharOffset);
      this.wordsCountInPara = parseInt(
        para.getAttribute(AppConstantsUtil.WORD_COUNT_ATTRIBUTE)
      );
      this.contentStableLength = TextUtils.calculateContentStableLength(para);
      this.startPosition = new Locator.InTextLocator(
        startParaId,
        logicalCharOffset
      );
      this.currentWordIndex = wordElementsOffsetsIndex;
      return isSetNextPara;
    } catch (error) {
      logger.warn(`Get error on fill paragraph data error: ${error}`);
      isSetNextPara = false;
    }
    return isSetNextPara;
  }

  isTargetParagraphRead() {
    return this.wordElementsOffsets.length === 0;
  }

  getLastReadLocator(locator) {
    let lastParaId = locator.prefixedParagraphId;
    let lastLogicalCharOffset = locator.logicalCharOffset;
    const trackable = this._isTrackablePara(lastParaId);
    if (trackable) {
      return locator;
    }
    const nextParaId = this._getNextParaId(lastParaId);
    if (!nextParaId) {
      return null;
    }
    lastLogicalCharOffset = 0;
    const lastReadLocator = new Locator.InTextLocator(
      nextParaId,
      lastLogicalCharOffset
    );
    return lastReadLocator;
  }

  _getAllWordsFromPara(para, readRanges) {
    const preparedText = TextUtils.extractContent(para);
    const wordsStableOffsets = TextUtils.collectWordsStableOffsets(
      preparedText
    );
    if (!readRanges) {
      return wordsStableOffsets;
    }
    for (let i = readRanges.length - 1; i >= 0; i--) {
      const readRange = readRanges[i];

      let startPosition = wordsStableOffsets.length - 1;
      for (let j = startPosition; j >= 0; j--) {
        const wordsStableOffset = wordsStableOffsets[j];

        const wordAfterRange =
          wordsStableOffset[0] >= readRange.endLocator.logicalCharOffset;

        const wordBeforeRange =
          wordsStableOffset[1] <= readRange.startLocator.logicalCharOffset;

        const isInsideRange = !wordAfterRange && !wordBeforeRange;

        if (wordBeforeRange) {
          startPosition = j;
          break;
        }

        if (isInsideRange) {
          wordsStableOffsets.splice(j, 1);
        }
      }
    }
    return wordsStableOffsets;
  }

  _getNextParaId(paraId) {
    let nextParaId = paraId;
    let found = false;
    while (!found && nextParaId) {
      nextParaId = this.store.getters['PublicationStore/getNextParaId'](
        nextParaId
      );

      found = this._isTrackablePara(nextParaId);
    }
    return nextParaId;
  }

  _isTrackablePara(paraId) {
    const para = document.getElementById(paraId);
    const isEmpty = !para || para.textContent.trim().length === 0;
    const isRead = this._isReadPara(paraId);
    return !isRead && !isEmpty;
  }

  _isReadPara(paraId) {
    const isRead = this.store.getters['ProgressStore/isReadParagraph'](
      this.publicationType,
      this.bookId,
      paraId
    );
    this.store.dispatch('ProgressStore/applyParaHighlightIfNeed', {
      paraId,
      isRead
    });
    return isRead;
  }

  $_getWordByIndex(index) {
    return this.wordElements[index];
  }
}

export default {
  props: {
    bookId: {
      type: String,
      required: true
    },
    publicationType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      tracker: null,
      readingSpeed: 60,
      currentRange: null,
      READING_AREA_UPDATED_ACTION: 'ProgressStore/updateReadingArea',
      trackingInitializerTimeout: null,
      positionTrackingState: TrackerStateEnum.STOP,
      endActionSubscription: null
    };
  },
  computed: {
    readParagraphRanges() {
      return this.$store.getters['ProgressStore/getReadParagraphRanges'];
    },
    currentScrubberLocator() {
      const serializeLocator = this.$store.getters[
        'PlaybackStore/getCurrentLocator'
      ];
      if (serializeLocator) {
        return Locator.deserialize(serializeLocator);
      }
      return serializeLocator;
    },
    readingArea() {
      return this.$store.getters['ProgressStore/getReadingArea'];
    },
    currentTrackingTask() {
      return this.$store.getters['ProgressStore/getCurrentTrackingTask'];
    },
    isAudioPlaying() {
      return this.$store.getters['PlaybackStore/isAudioPlaying'];
    }
  },
  watch: {
    readingArea(newReadingArea) {
      const shouldNotTrackProgress = this.$store.getters[
        'ContextStore/shouldNotTrackProgress'
      ];
      if (shouldNotTrackProgress) {
        return;
      }
      if (newReadingArea) {
        this.startPositionTracking();
      }
    },
    currentTrackingTask(task) {
      if (!task?.type) {
        return;
      } else {
        this.clearTrackingTimeout();
      }

      switch (task.type) {
        case ProgressTrackingTask.START_POSITION_TRACKER:
          if (!this.isAudioPlaying) {
            this.startTrackingRP();
          }
          this.$store.commit('ProgressStore/removeTrackingTask', task);
          break;
        case ProgressTrackingTask.START_DELAYED_POSITION_TRACKER:
          {
            const { startTrackingDelay } = task.payload;
            if (!this.isAudioPlaying) {
              this.startPositionTrackerWithDelay(startTrackingDelay);
            }
            this.$store.commit('ProgressStore/removeTrackingTask', task);
          }
          break;
        case ProgressTrackingTask.STOP_POSITION_TRACKER:
          this.stopTracking();
          this.$store.commit('ProgressStore/removeTrackingTask', task);
          break;
        default:
          break;
      }
    }
  },
  mounted() {
    this.endSubscription = this.$store.subscribe(mutation => {
      if (mutation.type === 'PublicationStore/setBookScrollEvent') {
        const { data, type } = mutation?.payload ?? {};
        if ([BookScrollEnum.ON_READING_AREA_CHANGE].includes(type)) {
          if (data?.isTooFar) {
            this.stopPositionTracker();
          }
        }
      }
    });
  },
  beforeDestroy() {
    this.$_stopTrackers();
  },
  destroyed() {
    if (this.endSubscription) {
      this.endSubscription();
    }
  },
  methods: {
    startPositionTracking() {
      this.$store.commit('ProgressStore/setTrackingTask', {
        trackingTasks: [
          {
            type: ProgressTrackingTask.START_DELAYED_POSITION_TRACKER,
            payload: {}
          }
        ]
      });
    },
    stopPositionTracker() {
      this.$store.commit('ProgressStore/setTrackingTask', {
        trackingTasks: [
          {
            type: ProgressTrackingTask.STOP_POSITION_TRACKER,
            payload: {}
          }
        ]
      });
    },
    startPositionTrackerWithDelay(externalStartDelay) {
      let startTrackingDelay = externalStartDelay;
      if (!externalStartDelay) {
        startTrackingDelay =
          this.positionTrackingState === TrackerStateEnum.START ||
          this.positionTrackingState === TrackerStateEnum.START_PENDING
            ? AppConstantsUtil.START_POSITION_TRACKER_SHORT_DELAY
            : AppConstantsUtil.START_POSITION_TRACKER_DELAY;
      }

      this.stopTracking();
      this.initPositionTracking(startTrackingDelay);
    },
    initPositionTracking(startTrackingDelay) {
      if (this.positionTrackingState !== TrackerStateEnum.START) {
        this.positionTrackingState = TrackerStateEnum.START_PENDING;
        this.clearTrackingTimeout();
        this.trackingInitializerTimeout = setTimeout(
          this.trackingInitializer,
          startTrackingDelay || AppConstantsUtil.START_POSITION_TRACKER_DELAY
        );
      }
    },
    trackingInitializer() {
      if (!this.isAudioPlaying) {
        this.startTrackingRP();
      }
    },
    startTrackingRP() {
      this.positionTrackingState = TrackerStateEnum.START;
      const highlightClass = this.$store.getters[
        'ProgressStore/getDefaultHighLightClass'
      ];
      this.tracker = new PositionTracker(
        this.readingSpeed,
        this.bookId,
        this.publicationType,
        'fast',
        {
          store: this.$store,
          highlightClass
        }
      );
      const locator = this.$_getStartLocator();
      if (!locator) {
        return;
      }
      const lastReadLocator = this.tracker.getLastReadLocator(locator);
      if (!lastReadLocator || !this.isInsideReadingArea(lastReadLocator)) {
        return;
      }
      const startParaId = lastReadLocator.prefixedParagraphId;
      const startLogicalCharOffset = lastReadLocator.logicalCharOffset;

      const isSetNextPara = this.tracker.fillParagraphData(
        startParaId,
        startLogicalCharOffset,
        this.readParagraphRanges[startParaId]
      );
      if (!isSetNextPara || this.tracker.isTargetParagraphRead()) {
        this.$_stopTrackers();
        return;
      }
      this.initCurrentRange(
        startParaId,
        this.tracker.position.logicalCharOffset
      );

      const readWordTime = this.tracker.getReadWordTime() || 100;
      this.tracker.scheduled = setTimeout(
        this.movePosition,
        readWordTime,
        this.tracker
      );
    },
    $_getStartLocator() {
      return this.readingArea?.startLocator;
    },
    stopTracking() {
      if (this.positionTrackingState === TrackerStateEnum.STOP) {
        return;
      }
      this.positionTrackingState = TrackerStateEnum.STOP;
      this.$_stopTrackers();
      this.$_saveCurrentParaRange(this.tracker);
    },
    initCurrentRange(startParaId, logicalCharOffset) {
      const startLocator = new Locator.InTextLocator(
        startParaId,
        logicalCharOffset
      );
      const endLocator = new Locator.InTextLocator(
        startParaId,
        logicalCharOffset
      );
      this.currentRange = UserProgressFactory.createProgressRange(
        startLocator,
        endLocator,
        'reading'
      );
    },
    clearTrackingTimeout() {
      window.clearTimeout(this.trackingInitializerTimeout);
    },
    movePosition(tracker) {
      if (
        !tracker.position.equals(this.readingArea.endLocator) &&
        this.isInsideReadingArea(tracker.position)
      ) {
        this.updateCurrentLocator(tracker);
        const isSetNextPosition = this.$_setNextPosition(tracker);
        if (!isSetNextPosition) {
          return;
        }
        const timeToReadWord = tracker.getReadWordTime();
        tracker.scheduled = setTimeout(
          this.movePosition,
          timeToReadWord,
          tracker
        );
      }
    },

    isInsideReadingArea(InTextLocator) {
      return this.$store.getters['ProgressStore/isInsideReadingArea'](
        InTextLocator
      );
    },

    updateCurrentRange(tracker) {
      if (this.currentRange === null) {
        this.currentRange = UserProgressFactory.createProgressRange(
          tracker.position,
          tracker.position,
          'reading'
        );
        return;
      }
      const currentLocator = this.currentRange?.range;
      const isTrackerHasNextPosition =
        currentLocator &&
        currentLocator.endLocator.precedes(tracker.position) &&
        currentLocator.startLocator.precedes(tracker.position);

      if (isTrackerHasNextPosition) {
        Object.assign(this.currentRange.range.endLocator, tracker.position);
      }
    },
    updateUserActivityTrack(tracker) {
      const wordsCount = tracker.wordsCountInPara;
      this.$store.dispatch('ProgressStore/updateUserActivityTracker', {
        publicationId: this.bookId,
        type: this.publicationType,
        tracker,
        newRange: this.currentRange,
        wordsCount
      });
    },
    updateCurrentLocator(tracker) {
      this.$store.commit('ProgressStore/setReadingCurrentLocator', {
        bookId: this.bookId,
        type: this.publicationType,
        currentLocator: Locator.serialize(tracker.position)
      });
    },
    $_saveCurrentParaRange(tracker) {
      if (
        !tracker ||
        !tracker.startPosition ||
        tracker.startPosition.equals(tracker.position)
      ) {
        return;
      }

      this.$store.commit('ProgressStore/saveReadParaRange', {
        startRangeLocator: tracker.startPosition,
        endRangeLocator: tracker.position
      });
    },
    $_stopTrackers() {
      window.clearTimeout(this.tracker && this.tracker.scheduled);
    },
    $_highLightIfNeed(tracker) {
      try {
        if (!tracker.position) {
          return;
        }
        const paraId = tracker.position.prefixedParagraphId;
        const wordOffset =
          tracker.wordElementsOffsets[tracker.currentWordIndex];
        this.$store.dispatch('ProgressStore/highlightColorWordsIfNeed', {
          paraId,
          wordOffset
        });
      } catch (error) {
        logger.warn(`Get error on highLight word by position error:${error}`);
      }
    },
    $_setNextPosition(tracker) {
      try {
        this.$_highLightIfNeed(tracker);
        this.updateCurrentRange(tracker);
        if (!tracker.isLast()) {
          tracker.setNextWord();
        } else {
          tracker.position.logicalCharOffset = tracker.contentStableLength;
          this.updateUserActivityTrack(tracker);
          const isSetNextPara = tracker.setNextPara(this.readParagraphRanges);
          if (!isSetNextPara) {
            this.$_stopTrackers();
            return false;
          }
          if (tracker.type === 'fast') {
            this.currentRange = null;
          }
        }
        return true;
      } catch (error) {
        logger.warn(`Get error on $_setNextPosition error:${error}`);
      }
      return false;
    },
    $_positionTrackerErrorHandler(error) {
      logger.fatal(`Error in PositionTracker: ${error.stack}`);
      this.$emit('positionTrackerEvent', {
        type: PositionTrackerEvents.ERROR,
        data: { error }
      });
    }
  }
};
</script>

<style lang="less" src="./PositionTracker.less"></style>
